import React, { memo, useCallback, useMemo } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  Button,
  HStack,
  Text,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useTags } from "modules/providers/tags-provider";
import colors from "app/colors";
import { useMutation } from "@apollo/client";
import {
  IParentTag,
  ITagCategory,
} from "modules/providers/tags-provider/tags-provider";
import { useMixpanel } from "modules/providers/mixpanel-provider";
import { HASH_KEY, TAG_GROUP_THREADS_PATH } from "modules/utils/Constants";
import { MIXPANEL_EVENT_TYPE } from "modules/providers/mixpanel-provider/mixpanel-provider";
import ExpandCollapseAccordionButton from "./expand-collapse-accordion-button";
import useFilterForSelectedState from "./use-filter-for-selected-state";
import {
  EditTagSeenMutation,
  EditTagSeenMutationVariables,
  TagFragment,
  SearchFilterType,
} from "gql/graphql";
import { FolderHashtagIcon, TagIcon } from "modules/icons";
import LeftNavInactiveTags from "./left-nav-inactive-tags";
import useSearchFilter from "modules/hooks/use-search-filter";
import useAppState from "modules/hooks/use-app-state";
import selectedTagGroupNavLinkProps from "./selected-tag-group-nav-link-props";
import EDIT_TAG_SEEN_MUTATION from "modules/layouts/_networking/edit-tag-seen";
import idsToIndexes from "modules/utils/ids-to-indexes";
import Link from "modules/layouts/_shared/link";

const unseenColor = colors.whiteTertiary;
const seenColor = colors.gray30;

const LeftNavTags = () => {
  const { leftNavActiveTagCategories } = useTags();
  const filterForSelectedState = useFilterForSelectedState();
  const { track } = useMixpanel();
  const { getUrl } = useSearchFilter();

  const {
    arrayValue: expandedTagCategoryIds,
    updateArray: setExpandedTagCategoryIds,
  } = useAppState({
    appStateType: "expandedTagCategoryIds",
  });

  const [editTagSeenAPI] = useMutation<
    EditTagSeenMutation,
    EditTagSeenMutationVariables
  >(EDIT_TAG_SEEN_MUTATION);

  const { booleanValue: isExpanded, updateBoolean: setIsExpanded } =
    useAppState({
      appStateType: "isTagsExpanded",
    });

  const {
    arrayValue: expandedParentTagIds,
    updateArray: setExpandedParentTagIds,
  } = useAppState({
    appStateType: "expandedParentTagIds",
  });

  const expandedTagCategoryIndexes = useMemo(
    () => idsToIndexes(expandedTagCategoryIds, leftNavActiveTagCategories),
    [expandedTagCategoryIds, leftNavActiveTagCategories],
  );

  const expandedParentTagIndexes = useMemo(
    () =>
      leftNavActiveTagCategories.map((tagCategory) =>
        idsToIndexes(expandedParentTagIds, tagCategory.parentTags),
      ),
    [expandedParentTagIds, leftNavActiveTagCategories],
  );

  const toggleTagCategory = useCallback(
    (id: string, expandOnly: boolean) => () => {
      const _expandedTagCategoryIds = [...expandedTagCategoryIds];
      if (_expandedTagCategoryIds.includes(id)) {
        if (!expandOnly) {
          setExpandedTagCategoryIds(
            _expandedTagCategoryIds.filter((v) => v !== id),
          );
          return;
        }
      }
      setExpandedTagCategoryIds([...expandedTagCategoryIds, id]);
    },
    [expandedTagCategoryIds, setExpandedTagCategoryIds],
  );

  const toggleParentTag = useCallback(
    (id: string, expandOnly: boolean) => () => {
      const _expandedParentTagIds = [...expandedParentTagIds];
      if (_expandedParentTagIds.includes(id)) {
        if (!expandOnly) {
          setExpandedParentTagIds(
            _expandedParentTagIds.filter((v) => v !== id),
          );
          return;
        }
      }
      setExpandedParentTagIds([...expandedParentTagIds, id]);
    },
    [expandedParentTagIds, setExpandedParentTagIds],
  );

  const onTagClicked = useCallback(
    (tag: TagFragment, canExpand: boolean) => () => {
      // In this case, we only want to check if the current tag is unseen, we do not care if this is a
      // parent tag with unseen child tags kind of situation.
      if (tag.isUnseen) {
        editTagSeenAPI({
          variables: {
            id: tag.id,
            isSeen: true,
          },
          optimisticResponse: {
            editTag: {
              __typename: "Tag",
              id: tag.id,
              isUnseen: false,
            },
          },
        });
      }

      if (canExpand) {
        toggleParentTag(tag.id, true)();
      }

      track(MIXPANEL_EVENT_TYPE.LINK, {
        "Link Location": "Left Hand Nav",
        "Link Action": "Hashtag",
        "Link Name": `${HASH_KEY}${tag.displayName}`,
      });
    },
    [editTagSeenAPI, toggleParentTag, track],
  );

  const childTagIsUnseen = useCallback(
    (childTag: TagFragment) => childTag.isUnseen,
    [],
  );

  const parentTagIsUnseen = useCallback(
    (parentTag: IParentTag) =>
      parentTag.isUnseen || parentTag.childTags.some(childTagIsUnseen),
    [childTagIsUnseen],
  );

  const tagCategoryIsUnseen = useCallback(
    (tagCategory: ITagCategory) =>
      tagCategory.parentTags.some(parentTagIsUnseen),
    [parentTagIsUnseen],
  );

  const toggleExpand = useCallback(
    (expandOnly: boolean) => () =>
      setIsExpanded(expandOnly ? true : !isExpanded),
    [isExpanded, setIsExpanded],
  );

  return (
    <Accordion index={isExpanded ? [0] : []} w="full">
      <AccordionItem border="none">
        {({ isExpanded }) => (
          <>
            <h1>
              <HStack spacing={0} py={1}>
                <ExpandCollapseAccordionButton
                  isExpanded={isExpanded}
                  canExpand
                  onClick={toggleExpand(false)}
                />
                <Button variant="link" ml={-1} onClick={toggleExpand(false)}>
                  <HStack>
                    <TagIcon fill={colors.orangePrimary} />
                    <Text flexShrink={1} fontSize={14} color={colors.gray30}>
                      Tags
                    </Text>
                  </HStack>
                </Button>
              </HStack>
            </h1>
            <AccordionPanel py={0} pl={5} pr={0}>
              <Accordion allowMultiple index={expandedTagCategoryIndexes}>
                {leftNavActiveTagCategories.map(
                  (tagCategory, tagCategoryIndex) => (
                    <AccordionItem key={tagCategory.id} border="none">
                      {({ isExpanded }) => (
                        <>
                          <h1>
                            <HStack spacing={1} py={1}>
                              <ExpandCollapseAccordionButton
                                isExpanded={isExpanded}
                                canExpand={!!tagCategory.parentTags.length}
                                {...(!!tagCategory.parentTags.length && {
                                  onClick: toggleTagCategory(
                                    tagCategory.id,
                                    false,
                                  ),
                                })}
                              />
                              <Button
                                variant="link"
                                w="full"
                                {...(!!tagCategory.parentTags.length && {
                                  onClick: toggleTagCategory(
                                    tagCategory.id,
                                    true,
                                  ),
                                })}
                              >
                                <HStack spacing={1} w="full">
                                  <FolderHashtagIcon fill={seenColor} />
                                  <Text
                                    flex={1}
                                    textAlign="left"
                                    color={
                                      tagCategoryIsUnseen(tagCategory)
                                        ? unseenColor
                                        : seenColor
                                    }
                                    fontSize={14}
                                  >
                                    {tagCategory.name}
                                  </Text>
                                </HStack>
                              </Button>
                            </HStack>
                          </h1>
                          <AccordionPanel py={0} pr={0}>
                            <Accordion
                              allowMultiple
                              index={expandedParentTagIndexes[tagCategoryIndex]}
                            >
                              {tagCategory.parentTags.map((parentTag) => (
                                <AccordionItem key={parentTag.id} border="none">
                                  {({ isExpanded }) => (
                                    <>
                                      <h1>
                                        <HStack spacing={1}>
                                          <ExpandCollapseAccordionButton
                                            isExpanded={isExpanded}
                                            canExpand={
                                              !!parentTag.childTags.length
                                            }
                                            {...(!!parentTag.childTags
                                              .length && {
                                              onClick: toggleParentTag(
                                                parentTag.id,
                                                false,
                                              ),
                                            })}
                                          />
                                          <Link
                                            href={getUrl(
                                              TAG_GROUP_THREADS_PATH,
                                              undefined,
                                              {
                                                type: SearchFilterType.TagIdList,
                                                objectIdListValue: [
                                                  parentTag.id,
                                                ],
                                              },
                                            )}
                                            onClick={onTagClicked(
                                              parentTag,
                                              !!parentTag.childTags.length,
                                            )}
                                            fontSize={14}
                                            w="full"
                                            py={0.5}
                                            color={
                                              parentTagIsUnseen(parentTag)
                                                ? unseenColor
                                                : seenColor
                                            }
                                            fontWeight={
                                              parentTagIsUnseen(parentTag)
                                                ? 750
                                                : 400
                                            }
                                            {...(filterForSelectedState?.id ===
                                              parentTag.id &&
                                              selectedTagGroupNavLinkProps)}
                                          >
                                            <HStack spacing={1}>
                                              <TagIcon fill={colors.grayMain} />
                                              <Text flex={1} fontSize={14}>
                                                {parentTag.name}
                                              </Text>
                                            </HStack>
                                          </Link>
                                        </HStack>
                                      </h1>
                                      <AccordionPanel py={0} pl={6} pr={0}>
                                        <Wrap
                                          spacing={1}
                                          py={
                                            parentTag.childTags.length ? 0.5 : 0
                                          }
                                          pr={4} // keeps child tags away from parent tag unread counts
                                        >
                                          {parentTag.childTags.map(
                                            (childTag) => (
                                              <WrapItem key={childTag.id}>
                                                <Link
                                                  href={getUrl(
                                                    TAG_GROUP_THREADS_PATH,
                                                    undefined,
                                                    {
                                                      type: SearchFilterType.TagIdList,
                                                      objectIdListValue: [
                                                        childTag.id,
                                                      ],
                                                    },
                                                  )}
                                                  onClick={onTagClicked(
                                                    childTag,
                                                    false,
                                                  )}
                                                  borderWidth="1px"
                                                  color={
                                                    childTagIsUnseen(childTag)
                                                      ? unseenColor
                                                      : seenColor
                                                  }
                                                  borderColor={
                                                    childTagIsUnseen(childTag)
                                                      ? unseenColor
                                                      : seenColor
                                                  }
                                                  rounded="full"
                                                  px={1}
                                                  fontSize={10}
                                                  {...(filterForSelectedState?.id ===
                                                    childTag.id && {
                                                    ...selectedTagGroupNavLinkProps,
                                                    borderColor:
                                                      selectedTagGroupNavLinkProps.color,
                                                    fontWeight: 700,
                                                    rounded: "full",
                                                  })}
                                                >
                                                  {childTag.name}
                                                </Link>
                                              </WrapItem>
                                            ),
                                          )}
                                        </Wrap>
                                      </AccordionPanel>
                                    </>
                                  )}
                                </AccordionItem>
                              ))}
                            </Accordion>
                          </AccordionPanel>
                        </>
                      )}
                    </AccordionItem>
                  ),
                )}
                <LeftNavInactiveTags />
              </Accordion>
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Accordion>
  );
};

export default memo(LeftNavTags);
